import { Component, Input, AfterViewInit, ViewEncapsulation } from '@angular/core';
import { DocumentService, FileDownloadService, UsageLoggingService, EventLoggingService } from  '../../_services';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DocumentAttributesModel } from '../../_interfaces/document/document.model';
import { EventLogAttributes } from '../../_interfaces/event-log/eventlog.model';
import { AppState } from '../../app.service';
import { CleanTextPipe } from '../../_utilities/clean.text.pipe';
import { TopicSlideItem, TopicSlideModel } from '../../_interfaces/topic-slide/topic-slide.model';

declare var dataLayer: any;

@Component({
  selector: 'pq-document-list',
  templateUrl: './document.list.component.html',
  styleUrls: ['./_document.list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DocumentListComponent implements AfterViewInit {
  @Input() public topResearchTopic: TopicSlideItem = {
    docId: undefined,
    attributes: undefined
  };
  @Input() public page: number = 1;
  @Input() public images: ImageRow[] = [];
  @Input() public relatedTopicsData: TopicSlideModel = { slide: [] };
  @Input() public relatedSearchTitle: string = '';

  @Input() public searchId: string = '';
  @Input() public profile: string = this._appState.get('current_profile') || '';

  public docs: Object[] = [];
  public partialDocResults: Object[] = [];
  public showTopTopic: boolean = false;
  public showImageRow: boolean = false;
  public abstractCharLimit: number = 315;
  public titleCharLimit: number = 180;
  public viewpoints: Object[] = [];
  public topicCharLimit: number = 460;

  private _downloadCompleted: boolean = true;
  private _relatedSearches: Object[] = [];

  @Input() set relatedSearches (searches: Object[]) {
    this._relatedSearches =  searches;
  };

  get relatedSearches () {
    return this._relatedSearches;
  }

  constructor (
    private _docService: DocumentService,
    private _fileDownloadSvc: FileDownloadService,
    private _eventLogSvc: EventLoggingService,
    private _usageLoggingSvc: UsageLoggingService,
    private _appState: AppState,
    private _pqCleanText: CleanTextPipe
  ) {}

  @Input() set docResults (docs: Object[]) {
    this.showTopTopic = (this.page === 1) && (docs.length > 0) && (this._appState.get('anyFilterApplied') !== true);
    this.showImageRow = (this.page === 1) && (docs.length > 0) && (this._appState.get('typeFiltersApplied') !== true);

    // on page 1 results is spilt into two groups
    if (this.page === 1 && docs.length > 5) {
      this.docs = docs.slice(0, 5);
      this.partialDocResults = docs.slice(5);
    } else {
      this.docs = docs;
      this.partialDocResults = [];
    }

    if ((this._appState.get('current_profile') === 'sirsissuesresearcher' || this._appState.get('current_profile') === 'sirsdiscoverer') && (<any>this.topResearchTopic).attributes) {
      this.viewpoints = (<any>this.topResearchTopic).attributes.viewpoint;
    }
  }

  public ngAfterViewInit() {
    if (this.showTopTopic && (<any> this.topResearchTopic).attributes) {
      const logEventRequest = this._eventLogSvc.logEvent(this.buildEventAttributes());
      logEventRequest.catch((err: any) => {
        // debug only
        // console.log(err);
      });
      this.pushRTTileInResultsToGA('Returned');
    } else {
      this.pushRTTileInResultsToGA('No Entry');
    }
  }

  public pushRTTileInResultsToGA(status: string) {
    if (dataLayer && Array.isArray(dataLayer)) {
      dataLayer.push({'event': 'ResultsRTTileDisplay', 'label': status});
    }
  }

  public pushResultsDocClickToGA(docNo: string, title: string, sourceType: string, pubTitle: string) {
    if (dataLayer && Array.isArray(dataLayer)) {
      if (sourceType && pubTitle && sourceType.toLowerCase() === 'other sources'
        && pubTitle.toLowerCase() === 'proquest guided research: research topics') {
        if (this.page && this.page === 1) {
          dataLayer.push({
            'event': 'RTTileClick',
            'label': title,
            'position': docNo,
            'clickOrigin': 'First Page Result Object'
          });
        } else {
          dataLayer.push({
            'event': 'RTTileClick',
            'label': title,
            'position': docNo,
            'clickOrigin': 'Other Pages Result Object'
          });
        }
      } else {
        dataLayer.push({
          'event': 'ResultsDocClick',
          'label': this._pqCleanText.transform(title, true),
          'position': docNo
        });
      }
    }
  }

  public pushTopRTTileClickToGA(title: String, clickOrigin: String) {
    if (dataLayer && Array.isArray(dataLayer)) {
      dataLayer.push({
        'event': 'RTTileClick',
        'label': title,
        'position': 'top',
        'clickOrigin': clickOrigin
      });
    }
  }

  public handlePdfOnlyDoc (e, searchId: string, docNo: string) {
    e.preventDefault();
    let pathData = e.currentTarget.pathname.split('/');
    let goid = pathData[pathData.length - 1];
    this.generatePdfOnlyLink(goid, searchId);
    this.pushDocUseClickToGA('pdfOnly', docNo);
  }

  public handleWebOnlyDoc (e, attributes: DocumentAttributesModel, docNo: string) {
    e.preventDefault();
    let path = e.currentTarget.href;
    window.open(path);
    this._usageLoggingSvc.doLogUsage('onlinewebpage', attributes); // log print usage
    this.pushDocUseClickToGA('webOnly', docNo);
  }

  public generatePdfOnlyLink(docId: string, searchId: string): void {
    let request = this._docService.getEmsDocLink(docId, searchId)
      .pipe(
        map((emsResponse: Object) => {
          let mediaRetrieveResponse = emsResponse['DocRetrieveResponse'] || {};
          if (mediaRetrieveResponse['media']['src']) {
            window.open(mediaRetrieveResponse['media']['src'], '_blank');
          }
        }),
        catchError(this._handleFileDownloadError.bind(this))
      )
      .toPromise();
  }

  public generatePdfLink(docNo: string, docId: string, title?: string, searchId?: string): void {
    const fileType = 'application/pdf';
    if (this._downloadCompleted) {
      let request = this._docService.getPdfAvailableDocLink(docId, searchId)
        .pipe(
          catchError (this._handleFileDownloadError.bind(this)),
          switchMap((emsResponse: Object) => {
            this._downloadCompleted = false;
            let mediaRetrieveResponse = emsResponse['DocRetrieveResponse'] || {};

            if (mediaRetrieveResponse['media']) {
              return this._fileDownloadSvc.get(mediaRetrieveResponse['media']['src'], fileType, this._convert2PlainText(title));
            }

            this._downloadCompleted = true;
            request.unsubscribe(); // clean up the request on failure
            return new Observable(); // return empty observable on failure
          })
        )
        .subscribe(() => {
          this._downloadCompleted = true;
          request.unsubscribe(); // clean up the request on success
        });
    }
    this.pushDocUseClickToGA('pdfAvailable', docNo)
  }

  public trackByFn (index: number, item: object): number {
    return item['id'];
  }

  private _handleFileDownloadError (error: Response, request: Observable<any>): Observable<any> {
    this._downloadCompleted = true;
    alert('There was an error downloading this file. Please try again later.');

    if (error.status >= 400 && error.status <= 500) {
      return throwError(error.status + ' ' + error.statusText);
    }
    return throwError(request);
  }

  private _convert2PlainText (s: string): string {
    const pattern1 = /\[\[mkup\]\]/g;
    const pattern2 = /\<body[^>]*\>([^]*)\<\/body/m;
    const bodyTextArray = /<body/.test(s) ? s.match(pattern2) : [s]; // find text within body tag

    // if there is text within body tag, use it otherwise use default string
    s = bodyTextArray.length === 2 ? bodyTextArray[1] : s;

    s = s.replace(pattern1, '');

    return s.trim();
  }

  private buildEventAttributes(): EventLogAttributes {
    let eventLogAttr: EventLogAttributes  = new EventLogAttributes();

    if (this._appState.get('EVENTTYPES') !== 'undefined' && this._appState.get('EVENTTYPES') != null ) {
      let eventTypes = this._appState.get('EVENTTYPES');

      for (let eType of eventTypes) {
        if (eType.type === 'RT_TOPRESULT') {
          eventLogAttr.eventType = eType.id;
        }
      }
    }
    eventLogAttr.eventParameter = (<any> this.topResearchTopic).attributes.legacyID;
    eventLogAttr.message = 'Research Topic Top Result - ' + (<any> this.topResearchTopic).attributes.title;
    return eventLogAttr;
  }

  private pushDocUseClickToGA(useFor: string, docNo: string) {
    if (dataLayer && Array.isArray(dataLayer)) {
      dataLayer.push({'event': 'ResultsDocUseClick', 'label': useFor, 'position': docNo});
    }
  }
}
